import { GRADES } from './constants';
import { Group } from './types';

export type GroupDisplay = {
  line1: string;
  line2: string;
};

export const formatGroupDisplay = (group: Group): GroupDisplay => ({
  line1: `${group.grade} | ${group.groupName}`,
  line2: group.teachers,
});

// Compact one-line label for use in <Select> dropdowns. Drops teachers.
export const formatGroupShort = (group: Group): string =>
  `${group.grade} | ${group.groupName}`;

// Class line for the printed badge when the active event has
// `foldGradeIntoGroupName` on: the grade becomes a prefix on the group
// name ("3rd" + "A - Room 207" => "3rd A - Room 207") so the grade reads
// at the class line's larger font instead of on its own smaller line
// below. Deliberately space-joined rather than using the " | " separator
// of `formatGroupShort` — this string is printed on a label for parents
// and volunteers, not shown in a picker. Either part may be blank on
// hand-edited docs, so blank segments are dropped and the result is never
// left with a leading, trailing, or double space.
export const formatGroupForLabel = (group: Group): string =>
  [group.grade, group.groupName]
    .map((part) => part?.trim() ?? '')
    .filter((part) => part.length > 0)
    .join(' ');

// Best-effort count of teachers from the free-text `teachers` field.
// The field is comma-separated by convention (e.g. "Alex, Sam") but
// admins type it freehand, so we tolerate stray whitespace and empty
// segments. Used by the Kitchen PDF to compute group + rotation
// head-counts for meal prep.
export const countTeachers = (teachers: string | undefined): number => {
  if (!teachers) return 0;
  return teachers
    .split(',')
    .map((t) => t.trim())
    .filter((t) => t.length > 0).length;
};

// Canonical group ordering used everywhere groups are displayed: by grade
// (using the GRADES array order), then case-insensitive alphabetical by name
// within a grade. Unknown grades sort to the end (defensive guard against
// hand-edited Firestore data).
export const sortGroups = (list: Group[]): Group[] =>
  [...list].sort((a, b) => {
    const ai = GRADES.indexOf(a.grade) + 1 || GRADES.length + 1;
    const bi = GRADES.indexOf(b.grade) + 1 || GRADES.length + 1;
    const diff = ai - bi;
    if (diff !== 0) return diff;
    return a.groupName.localeCompare(b.groupName, undefined, {
      sensitivity: 'base',
    });
  });
